import { memo, PropsWithChildren, useEffect, useRef, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import drawFeatureName from "./helpers/drawFeatureName";
import drawDashedLine from "./helpers/drawDashedLine";
import drawShadow from "./helpers/drawShadow";
import config from "config.ts";

import styles from "./mainBanner.module.scss";

const FEATURES = [
  { name: "biz-sub-accounts-management", icon: "users" },
  { name: "biz-advanced-security-settings", icon: "security" },
  { name: "biz-customize-forms-for-receiving-files", icon: "form" },
  { name: "biz-customized-branding", icon: "brand" },
  { name: "biz-crm-to-manage-all-transfers", icon: "crm" },
  { name: "biz-unlimited-storage", icon: "storage" },
  { name: "biz-single-sign-on-sso", icon: "sso" },
  { name: "biz-compliance", icon: "complience" },
  { name: "biz-rich-gallery-presentation", icon: "gallery" },
  { name: "biz-private-webpage", icon: "webpage" },
  { name: "biz-get-paid-for-your-work", icon: "pay" },
  { name: "biz-advanced-analytics", icon: "logo" },
];
const radiuses = Array.from({ length: FEATURES.length }, () =>
  Math.floor(Math.random() * 34)
);
const DEFAULT_SCALE = 1;
const MAX_SCALE = 1.2;
const MIN_SCALE = 1;

let radius = 120; // Radius for the circular path
let angle = 0; // Initial angle
let speed = 0; // Speed of rotation

let attentionScale = 1;
let attentionFeatureIndex = Math.floor(Math.random() * (FEATURES.length - 1));
let isScaleUp = true;

let opacity = 0;

const imageUrls = FEATURES.map(
  (f) =>
    `${config.api.resource}/assets/for-biz-banner/${f.icon}.${
      f.icon === "logo" ? "png" : "svg"
    }`
);

const MainBanner = ({ children }: PropsWithChildren) => {
  const { getTextByKey, getDirection } = useContent();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const images = useRef<HTMLImageElement[]>([]);
  const [cursorXMov, setCursorXMov] = useState(0);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Get the cursor position on canvas
      const rect = canvasRef.current!.getBoundingClientRect();
      setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // get X cursor movement direction
      setCursorXMov(e.movementX);
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    // Change speed based on cursor movement
    if (!isHovered) {
      speed = 0;
    } else {
      speed = cursorXMov / 20000;
    }
  }, [cursorXMov, isHovered]);

  useEffect(() => {
    const canvas: HTMLCanvasElement = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    // canvas.style.backgroundColor = BG_COLOR;

    canvas.width = 1428;
    canvas.height = 580;

    let id: number;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2 - 25;

    // Load all images from the provided URLs
    const loadImages = () => {
      images.current = imageUrls.map((url) => {
        const img = new Image();
        img.src = url;

        return img;
      });
    };

    // Animation loop
    const render = () => {
      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update the angle for rotation
      angle += speed;

      if (radius < 200) {
        radius++;
      }

      if (opacity < 1) {
        opacity += 0.005;
      }

      // Draw each image in a circular pattern
      images.current.forEach((img, index) => {
        if (FEATURES[index].icon === "logo") {
          // draw logo in the center
          ctx.save();
          ctx.globalAlpha = opacity;

          const size = 150 * opacity;

          ctx.drawImage(
            img,
            centerX - size / 2,
            centerY - size / 2,
            size,
            size
          );

          ctx.fillStyle = "#fff";
          ctx.font = "20px Arial";
          ctx.textAlign = "center";
          ctx.fillText(
            getTextByKey("your-business").toUpperCase(),
            centerX,
            centerY + size / 2 + 40
          );

          ctx.restore();
        } else {
          const totalImages = images.current.length - 1;
          const currentAngle = angle + index * ((2 * Math.PI) / totalImages); // Spread images evenly

          // Calculate x, y positions based on circular path
          const x =
            centerX +
            (radius + radiuses[index]) * (Math.cos(currentAngle) * 1.7) -
            img.width / 2;
          const y =
            centerY +
            (radius + radiuses[index]) * Math.sin(currentAngle) -
            img.height / 2;
          ctx.save();
          ctx.globalAlpha = opacity;

          const textX = x + img.width / 2;
          const textY = y + img.height + 20;
          const featureName = getTextByKey(FEATURES[index].name);
          const lineX =
            centerX +
            (radius + radiuses[index]) * (Math.cos(currentAngle) * 1.7);
          const lineY =
            centerY + (radius + radiuses[index]) * Math.sin(currentAngle);

          const size =
            50 *
            (attentionFeatureIndex === index ? attentionScale : DEFAULT_SCALE);

          // Check if the cursor is over the image
          if (
            cursorPos.x > x &&
            cursorPos.x < x + size && // Adjusted to match the drawn image size
            cursorPos.y > y &&
            cursorPos.y < y + size // Adjusted to match the drawn image size
          ) {
            speed = 0;
            drawFeatureName(ctx, featureName, textX, textY, true);
            drawShadow(ctx, true);
            drawDashedLine(ctx, centerX, centerY, lineX, lineY, true);
          } else {
            drawFeatureName(ctx, featureName, textX, textY, false);
            drawDashedLine(ctx, centerX, centerY, lineX, lineY, false);
            drawShadow(ctx, false);
          }

          // Scale the image if it's the attention feature

          if (attentionFeatureIndex === index) {
            if (isScaleUp) {
              attentionScale += 0.001;
            } else {
              attentionScale -= 0.001;
            }

            if (attentionScale >= MAX_SCALE) {
              isScaleUp = false;
            } else if (attentionScale <= MIN_SCALE) {
              attentionFeatureIndex = Math.floor(
                Math.random() * (FEATURES.length - 1)
              );
              isScaleUp = true;
            }
          }

          ctx.drawImage(img, x, y, size, size); // Adjust size if needed (50x50 here)

          ctx.restore();
        }
      });

      // Request next animation frame
      id = requestAnimationFrame(render);
    };

    loadImages(); // Load the images
    render(); // Start the animation

    // Cleanup on unmount
    return () => {
      cancelAnimationFrame(id);
    };
  }, [cursorPos, getDirection()]);

  return (
    <canvas
      ref={canvasRef}
      width={500}
      height={500}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={styles.wrapper}
    >
      {children}
    </canvas>
  );
};

export default memo(MainBanner);
